import _ from 'lodash';
import {
  getLastFieldInPath,
  getObjectFromPath,
  getDependencyFromState,
} from 'common/utils';
import ReactHtmlParser from 'react-html-parser';
import {compileContent} from 'common/utils';
import {createSelector} from 'reselect';

/* Validation dirty map */
const getValidationDirtyMap = (state, validationIdProp) => {
  return _.get(state, `dynamicComponent.validationDirtyMap.${validationIdProp}`);
};

const getSubmissionAttempted = (state) => {
  return _.get(state, 'dynamicComponent.validationErrors.submissionAttempted');
};

const getValidationErrors = (state, validationIdProp) => {
  return _.get(state, `dynamicComponent.validationErrors.errors.${validationIdProp}`);
};

const selectData = (state) => _.get(state, 'dynamicComponent.data');
const selectContext = (state) => _.get(state, 'dynamicComponent.context');

const makeSelectValidationErrors = () => {
  return createSelector([getValidationErrors], (validationsErrors) => validationsErrors);
};

const makeSelectValidationDirtyMap = () => {
  return createSelector(
    [getValidationDirtyMap, getSubmissionAttempted],
    (dirty, getSubmissionAttempted) => {
      return {
        dirty: dirty || getSubmissionAttempted,
        dirtyPage: getSubmissionAttempted,
      };
    }
  );
};

const createMapStateToProps = () => {
  const selectValidationDirtyMap = makeSelectValidationDirtyMap();
  const selectValidationErrors = makeSelectValidationErrors();

  const mapStateToProps = (state, ownProps) => {
    let {
      dataPath,
      alternateData,
      prePopPath,
      dependencies,
      template,
      entityDataPath,
      templateAsArray,
      parseHtml,
      templatedProps,
    } = ownProps;

    let propsFromState = {};
    let prePopped = false;
    let data;
    let dataErrors;
    let pathToBeUsed;

    if (entityDataPath) {
      if (_.isString(entityDataPath)) {
        data = getObjectFromPath(state.dynamicComponent, entityDataPath); // not getDependencyFromState because we don't want to let them save outside of dynamicComponent
      } else if (_.isArray(entityDataPath)) {
        data = _.map(entityDataPath, (path) =>
          getObjectFromPath(state.dynamicComponent, path)
        );
      } // else don't load the data, bad configuration

      dataErrors = _.get(state, 'dynamicComponent.dataErrors.payload'); // DEPRECATED, use validationErrors instead for V2 validations
    } else if (dataPath) {
      // This else if branch is for legacy components only, don't use datapath or prePopPath
      const dataSource = alternateData || _.get(state, 'dynamicComponent.data.payload');
      data = getObjectFromPath(dataSource, dataPath);
      dataErrors = _.get(state, 'dynamicComponent.dataErrors.payload');

      if (prePopPath !== undefined && data === undefined) {
        data = getObjectFromPath(state.dynamicComponent, prePopPath);
        prePopped = true;
      } else if (data === undefined) {
        // try to get data from dynamicComponent/context
        data = getObjectFromPath(state.dynamicComponent, dataPath);
      }
    }

    /* add named dependencies to existing list of strings
        dependencies: ["a.b.c", { name: "myOrgId", path: "context.org.id"}, "data.programId"]
      */
    _.forEach(dependencies, (dependency) => {
      if (typeof dependency === 'object') {
        _.each(_.keys(dependency), (key) => {
          // allows you to use props to build a datapath { "myDependency1": "data.${myKey}.field1" }
          // in case of the dependency is an array
          if (_.isArray(dependency[key])) {
            propsFromState[key] = [];
            _.each(dependency[key], (myDep) => {
              let path = compileContent(myDep, {
                ...ownProps,
                data: data,
              });
              const pathValue = getDependencyFromState(state, path);
              propsFromState[key].push(pathValue);
            });
          } else {
            let compiledPath = compileContent(dependency[key], {
              ...ownProps,
              data: data,
            });
            if (!_.isEmpty(compiledPath)) {
              propsFromState[key] = getDependencyFromState(state, compiledPath);
            }
          }
        });
      } else {
        propsFromState[getLastFieldInPath(dependency)] = getDependencyFromState(
          state,
          dependency
        );
      }
    });

    /**
     * templatedProps: {
     *    "title": "${orgData.lendingAmount}"
     * }
     * Passes orgData.lendingAmount as the title prop. Overrides existing named props from dependencies and ownProps;
     * **/
    _.forEach(templatedProps, (templateValue, templateName) => {
      let htmltemplateFlag = parseHtml;
      if (_.isArray(parseHtml) && _.size(parseHtml) > 0) {
        htmltemplateFlag = _.includes(parseHtml, templateName);
      }
      // allows you to use props to build a datapath { "myDependency1": "data.${myKey}.field1" }
      let compiledTemplate = compileContent(
        templateValue,
        {
          ...ownProps,
          data,
          ...propsFromState,
        },
        htmltemplateFlag
      );
      propsFromState[templateName] = compiledTemplate;
    });

    propsFromState.prePopped = prePopped;

    /**  Associate component with its validation errors and validation dirty map (is the field blurred?) through its validation id **/
    let validationIdProp = _.get(
      propsFromState,
      'validationId',
      _.get(ownProps, 'validationId')
    );

    if (validationIdProp) {
      propsFromState.validationErrors = selectValidationErrors(state, validationIdProp);
      propsFromState.validationDirtyMap = selectValidationDirtyMap(
        state,
        validationIdProp
      );
    }

    /* If the entity datapath was a templated string, get that path now and load data correctly, TBD on whether this belongs here or above */
    const entityDataPathValue = entityDataPath || _.get(propsFromState, 'entityDataPath');

    if (entityDataPathValue && !entityDataPath) {
      // pass the proper "data" to the component based on it's entityDataPath
      data = getObjectFromPath(state.dynamicComponent, entityDataPathValue); // should probably be cleaned up
    }

    /* Convenience syntax for templatedProps (when you just need "data" templated, which is often) */
    if (template) {
      data = compileContent(
        template,
        {
          ...ownProps,
          data: data,
          ...propsFromState,
        },
        parseHtml,
        templateAsArray
      );
    }

    /* For backwards compatibility for components that just need the datapath and don't care which system is used */
    if (entityDataPathValue !== undefined) {
      pathToBeUsed = entityDataPathValue;
    } else {
      pathToBeUsed = dataPath;
    }

    propsFromState.allData = selectData(state); // this.props.allData SHOULD NOT BE USED, for legacy purposes only, TODO, purge this, make another connectDynamic/parameterize it without this as a stopgap.
    propsFromState.context = selectContext(state); // this.props.context SHOULD NOT BE USED, for legacy purposes only

    if (dataPath !== undefined) {
      propsFromState.data = data;
      propsFromState.dataErrors = dataErrors;
      propsFromState.pathToBeUsed = pathToBeUsed;
    } else if (template !== undefined) {
      propsFromState.data = data;
      propsFromState.pathToBeUsed = pathToBeUsed;
    } else if (entityDataPathValue) {
      propsFromState.data = data;
      propsFromState.dataErrors = dataErrors;
      propsFromState.pathToBeUsed = pathToBeUsed; // this.props.pathToBeUsed is for components that were built before 2020 only. New components should just use entityDataPath
    } else {
      propsFromState.pathToBeUsed = pathToBeUsed;
    }
    return propsFromState;
  };
  return mapStateToProps;
};

export default createMapStateToProps;
